import { Component, OnDestroy, OnInit } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Subscription } from 'rxjs';
import { RuoloReferenzaModel } from '../../core/models/api/ruolo-referenza.model';
import { ReferenzaFirestore } from '../../core/models/firestore/referenza.model';
import { ReferenzeService } from '../../services/referenze.service';
import { RuoliReferenzaService } from '../../services/ruoli-referenza.service';
import { UserRoleService } from '../../services/user-role.service';
import { ConfirmationService, MessageService } from 'primeng/api';

@Component({
  selector: 'app-ruoli-referenza',
  standalone: false,
  templateUrl: './ruoli-referenza.component.html',
  styleUrl: './ruoli-referenza.component.scss',
})
export class RuoliReferenzaComponent implements OnInit, OnDestroy {
  ruoli: RuoloReferenzaModel[] = [];
  filteredRuoli: RuoloReferenzaModel[] = [];
  loading = false;
  searchText = '';

  // Dialog creazione/modifica ruolo
  ruoloDialogVisible = false;
  dialogMode: 'create' | 'edit' = 'create';
  editingRuoloId: number | null = null;
  editingDescrizione: string = '';
  savingEdit = false;

  private ruoliSub?: Subscription;
  private referenzeSub?: Subscription;
  private ruoliBase: RuoloReferenzaModel[] = [];
  private referenze: ReferenzaFirestore[] = [];

  constructor(
    private ruoliReferenzaService: RuoliReferenzaService,
    private referenzeService: ReferenzeService,
    private logger: NGXLogger,
    private userRoleService: UserRoleService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.loadRuoli();
    this.referenzeSub = this.referenzeService.listenReferenze().subscribe({
      next: (referenze) => {
        this.referenze = referenze;
        this.updateRuoliWithReferenzeCounts();
      },
      error: (err) => {
        this.logger.error(
          '[RUOLI-REFERENZA] Errore nel caricamento delle referenze:',
          err,
        );
      },
    });
  }

  ngOnDestroy(): void {
    if (this.ruoliSub) {
      this.ruoliSub.unsubscribe();
    }
    if (this.referenzeSub) {
      this.referenzeSub.unsubscribe();
    }
  }

  canModify(): boolean {
    return this.userRoleService.canModify();
  }

  isViewer(): boolean {
    return this.userRoleService.isViewer();
  }

  /**
   * Applica il filtro di ricerca
   */
  applyFilter(): void {
    const term = this.searchText.toLowerCase().trim();

    if (!term) {
      this.filteredRuoli = [...this.ruoli];
    } else {
      this.filteredRuoli = this.ruoli.filter(
        (ruolo) =>
          ruolo.descrizione.toLowerCase().includes(term) ||
          ruolo.codice.toLowerCase().includes(term),
      );
    }
  }

  /**
   * Gestisce il cambio del testo di ricerca
   */
  onSearchChange(): void {
    this.applyFilter();
  }

  /**
   * Pulisce il filtro di ricerca
   */
  clearSearch(): void {
    this.searchText = '';
    this.applyFilter();
  }

  /**
   * Apre il dialog in modalità creazione
   */
  openCreateDialog(): void {
    if (!this.canModify()) return;
    this.dialogMode = 'create';
    this.editingRuoloId = null;
    this.editingDescrizione = '';
    this.savingEdit = false;
    this.ruoloDialogVisible = true;
  }

  /**
   * Avvia la modifica di un ruolo (usa la stessa maschera della creazione)
   */
  startEdit(ruolo: RuoloReferenzaModel): void {
    if (!this.canModify()) return;

    this.editingRuoloId = ruolo.id;
    this.editingDescrizione = ruolo.descrizione;
    this.dialogMode = 'edit';
    this.savingEdit = false;
    this.ruoloDialogVisible = true;
  }

  /**
   * Chiude il dialog e resetta lo stato di editing
   */
  cancelEdit(): void {
    this.editingRuoloId = null;
    this.editingDescrizione = '';
    this.savingEdit = false;
    this.ruoloDialogVisible = false;
  }

  /**
   * Verifica se esiste già un ruolo con la stessa descrizione (case-insensitive)
   */
  private isDescrizioneDuplicate(
    descrizione: string,
    excludeId: number | null,
  ): boolean {
    const normalized = descrizione.trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    return this.ruoli.some(
      (r) =>
        r.id !== excludeId && r.descrizione.trim().toLowerCase() === normalized,
    );
  }

  /**
   * Salva il ruolo (creazione o modifica)
   */
  saveRuolo(): void {
    const descrizione = this.editingDescrizione.trim();
    if (!descrizione) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore Validazione',
        detail: 'La descrizione è obbligatoria',
        life: 5000,
      });
      return;
    }

    if (
      this.isDescrizioneDuplicate(
        descrizione,
        this.dialogMode === 'edit' ? this.editingRuoloId : null,
      )
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore Validazione',
        detail: 'Esiste già un ruolo con questa descrizione',
        life: 5000,
      });
      return;
    }

    this.savingEdit = true;

    if (this.dialogMode === 'create') {
      this.ruoliReferenzaService.createRuolo({ descrizione }).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Ruolo creato con successo',
            life: 3000,
          });
          this.cancelEdit();
          this.loadRuoli();
        },
        error: (err) => {
          this.logger.error(
            '[RUOLI-REFERENZA] Errore nella creazione del ruolo:',
            err,
          );
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail:
              err.error?.message || 'Errore durante la creazione del ruolo',
            life: 5000,
          });
          this.savingEdit = false;
        },
      });
    } else if (this.dialogMode === 'edit' && this.editingRuoloId != null) {
      this.ruoliReferenzaService
        .updateRuolo(this.editingRuoloId, { descrizione })
        .subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Successo',
              detail: 'Ruolo aggiornato con successo',
              life: 3000,
            });
            this.cancelEdit();
            this.loadRuoli();
          },
          error: (err) => {
            this.logger.error(
              "[RUOLI-REFERENZA] Errore nell'aggiornamento del ruolo:",
              err,
            );
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail:
                err.error?.message ||
                "Errore durante l'aggiornamento del ruolo",
              life: 5000,
            });
            this.savingEdit = false;
          },
        });
    }
  }

  /**
   * Conferma ed elimina un ruolo
   */
  confirmDelete(ruolo: RuoloReferenzaModel): void {
    if (!this.canModify()) return;

    this.confirmationService.confirm({
      message: `
        <div class="delete-confirm-message">
          <p class="confirm-text">Sei sicuro di voler eliminare questo ruolo?</p>
          <div class="tag-details">
            <div class="detail-row">
              <span class="detail-value code-value">${ruolo.descrizione.toUpperCase()}</span>
            </div>
          </div>
          <p class="warning-text">Questa azione non può essere annullata.</p>
        </div>
      `,
      header: 'Conferma Eliminazione',
      icon: 'none',
      acceptLabel: 'Elimina',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.deleteRuolo(ruolo.id);
      },
    });
    /*
                  <span class="detail-label">Descrizione:</span>
 <div class="detail-row">
              <span class="detail-label">Codice:</span>
              <span class="detail-value code-value">${ruolo.codice}</span>
            </div> */
  }

  /**
   * Elimina un ruolo
   */
  private deleteRuolo(id: number): void {
    this.ruoliReferenzaService.deleteRuolo(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Ruolo eliminato con successo',
          life: 3000,
        });
        this.loadRuoli();
      },
      error: (err) => {
        this.logger.error(
          "[RUOLI-REFERENZA] Errore nell'eliminazione del ruolo:",
          err,
        );
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail:
            err.error?.message || "Errore durante l'eliminazione del ruolo",
          life: 5000,
        });
      },
    });
  }

  private loadRuoli(): void {
    this.loading = true;
    this.ruoliSub = this.ruoliReferenzaService.getRuoli().subscribe({
      next: (ruoli) => {
        this.ruoliBase = [...ruoli];
        this.updateRuoliWithReferenzeCounts();
        this.loading = false;
        this.logger.info(
          '[RUOLI-REFERENZA] Ruoli caricati:',
          this.ruoli.length,
        );
      },
      error: (err) => {
        this.logger.error(
          '[RUOLI-REFERENZA] Errore nel caricamento dei ruoli:',
          err,
        );
        this.loading = false;
      },
    });
  }

  private updateRuoliWithReferenzeCounts(): void {
    const counts = this.computeNumeroReferenzeByRuolo(this.referenze);
    this.ruoli = this.ruoliBase.map((ruolo) => ({
      ...ruolo,
      numeroReferenze: counts.get(ruolo.codice) ?? 0,
    }));
    this.applyFilter();
  }

  private computeNumeroReferenzeByRuolo(
    referenze: ReferenzaFirestore[],
  ): Map<string, number> {
    const counts = new Map<string, number>();

    for (const referenza of referenze) {
      const codiciRuolo = new Set(
        (referenza.ruoliProfili ?? []).map((rp) => rp.ruoloCodice),
      );

      for (const codice of codiciRuolo) {
        counts.set(codice, (counts.get(codice) ?? 0) + 1);
      }
    }

    return counts;
  }
}
